/**
 * The lazy page components, declared once, separately from the route table.
 *
 * Why split from routes.jsx: anything that wants to PRELOAD a page (PrefetchLink
 * callers, the app bar's section config) needs a handle on these components. If
 * they import them from routes.jsx they create a cycle — routes.jsx imports the
 * layout, the layout imports the app bar, the bar imports the section config,
 * the config imports routes.jsx — which `import/no-cycle` rejects and which
 * breaks tree-shaking.
 *
 * This module imports nothing from the app, so it is always a leaf: the route
 * table and link callers both depend on it, and never on each other.
 */
import { lazyWithPreload } from './lazyWithPreload';

// The two sign-in screens. routes.jsx picks ONE for the landing URL at module
// load (config/loginMode.js); the local form also has /login on every host.
export const HomePage = lazyWithPreload(() => import('@features/auth/routes/HomePage'));

export const LocalLoginPage = lazyWithPreload(() => import('@features/auth/routes/LocalLoginPage'));

export const ManagePortfolioPage = lazyWithPreload(
  () => import('@features/manageaccounts/routes/ManagePortfolio'),
);

export const TelemetryPage = lazyWithPreload(
  () => import('@features/telemetry/routes/TelemetryPage'),
);

// CCA's Cloud Usage Reports — legacy /dashboard/cloudreports.
export const CloudUsageReportsPage = lazyWithPreload(
  () => import('@features/cloudusagereports/routes/CloudUsageReportsPage'),
);

export const ExplorerPage = lazyWithPreload(() => import('@features/explorer/routes/ExplorerPage'));

export const AnalyticsV2Page = lazyWithPreload(
  () => import('@features/analytics/routes/AnalyticsV2Page'),
);

// Instance Advice (EIA) / Cost Advice (CCA) — legacy /dashboard/costadvice.
export const CostAdvicePage = lazyWithPreload(
  () => import('@features/costadvice/routes/CostAdvicePage'),
);

export const SupportPage = lazyWithPreload(() => import('@features/support/routes/SupportPage'));

export const ReleaseNotesPage = lazyWithPreload(
  () => import('@features/support/releaseNotes/ReleaseNotesPage'),
);

export const RegionsListPage = lazyWithPreload(
  () => import('@features/regions/routes/RegionsListPage'),
);

export const NotFoundPage = lazyWithPreload(() => import('@components/common/NotFoundPage'));
